import React, { useState, useEffect } from 'react';
import {
  Paper,
  Typography,
  Box,
  Button,
  Card,
  CardContent,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  IconButton,
  Alert,
  LinearProgress,
  Tooltip,
  InputAdornment,
} from '@mui/material';
import {
  Add,
  Edit,
  Delete,
  Receipt,
  AttachMoney,
  Category,
  DateRange,
  Business,
  CloudUpload,
  Visibility,
  GetApp,
} from '@mui/icons-material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs, { Dayjs } from 'dayjs';
import { apiService } from '../services/api';
import { useBrandColors } from '../hooks/useBrandColors';

interface Expense {
  id: string;
  amount: number;
  currency: string;
  description: string;
  category: string;
  date: string;
  projectId?: string;
  projectName?: string;
  clientId?: string;
  clientName?: string;
  receiptUrl?: string;
  notes?: string;
  status: 'pending' | 'approved' | 'rejected';
  createdAt: string;
  updatedAt: string;
}

interface ExpenseFormData {
  amount: string;
  currency: string;
  description: string;
  category: string;
  date: Dayjs | null;
  projectId: string;
  clientId: string;
  notes: string;
}

const expenseCategories = [
  'Travel',
  'Meals',
  'Office Supplies',
  'Software',
  'Equipment',
  'Marketing',
  'Training',
  'Utilities',
  'Other'
];

const currencies = ['USD', 'EUR', 'GBP', 'CAD', 'AUD'];

export const Expenses: React.FC = () => {
  const brandColors = useBrandColors();
  const [expenses, setExpenses] = useState<Expense[]>([]);
  const [projects, setProjects] = useState<any[]>([]);
  const [clients, setClients] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingExpense, setEditingExpense] = useState<Expense | null>(null);
  const [formData, setFormData] = useState<ExpenseFormData>({
    amount: '',
    currency: 'USD',
    description: '',
    category: '',
    date: dayjs(),
    projectId: '',
    clientId: '',
    notes: ''
  });

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    setLoading(true);
    setError(null);
    try {
      const [expensesData, projectsData, clientsData] = await Promise.all([
        apiService.getExpenses(),
        apiService.getProjects(),
        apiService.getClients()
      ]);
      
      setExpenses(expensesData);
      setProjects(projectsData);
      setClients(clientsData);
    } catch (error) {
      console.error('Failed to load data:', error);
      setError(error instanceof Error ? error.message : 'Failed to load expenses');
    } finally {
      setLoading(false);
    }
  };

  const handleOpenDialog = (expense?: Expense) => {
    if (expense) {
      setEditingExpense(expense);
      setFormData({
        amount: expense.amount.toString(),
        currency: expense.currency,
        description: expense.description,
        category: expense.category,
        date: dayjs(expense.date),
        projectId: expense.projectId || '',
        clientId: expense.clientId || '',
        notes: expense.notes || ''
      });
    } else {
      setEditingExpense(null);
      setFormData({
        amount: '',
        currency: 'USD',
        description: '',
        category: '',
        date: dayjs(),
        projectId: '',
        clientId: '',
        notes: ''
      });
    }
    setDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    setEditingExpense(null);
  };

  const handleSubmit = async () => {
    try {
      const expenseData = {
        amount: parseFloat(formData.amount),
        currency: formData.currency,
        description: formData.description,
        category: formData.category,
        date: formData.date?.toISOString() || new Date().toISOString(),
        projectId: formData.projectId || undefined,
        clientId: formData.clientId || undefined,
        notes: formData.notes || undefined
      };

      if (editingExpense) {
        await apiService.updateExpense(editingExpense.id, expenseData);
      } else {
        await apiService.createExpense(expenseData);
      }

      await loadData();
      handleCloseDialog();
    } catch (error) {
      console.error('Failed to save expense:', error);
      setError(error instanceof Error ? error.message : 'Failed to save expense');
    }
  };

  const handleDelete = async (id: string) => {
    if (!window.confirm('Are you sure you want to delete this expense?')) {
      return;
    }

    try {
      await apiService.deleteExpense(id);
      await loadData();
    } catch (error) {
      console.error('Failed to delete expense:', error);
      setError(error instanceof Error ? error.message : 'Failed to delete expense');
    }
  };

  const formatCurrency = (amount: number, currency: string) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currency
    }).format(amount);
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'approved':
        return 'success';
      case 'rejected':
        return 'error';
      default:
        return 'warning';
    }
  };

  const totalExpenses = expenses.reduce((sum, expense) => {
    // Convert to USD for total (simplified - in real app you'd use exchange rates)
    return sum + expense.amount;
  }, 0);

  const styles = {
    container: {
      backgroundColor: 'white',
      minHeight: '100vh',
      padding: '24px'
    },
    header: {
      display: 'flex',
      alignItems: 'center',
      gap: '12px',
      marginBottom: '32px'
    },
    title: {
      fontSize: '28px',
      fontWeight: '600',
      color: '#1a1a1a'
    },
    subtitle: {
      fontSize: '16px',
      color: '#666',
      marginBottom: '24px'
    },
    summaryGrid: {
      display: 'grid',
      gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
      gap: '16px',
      marginBottom: '32px'
    },
    summaryCard: {
      textAlign: 'center',
      padding: '20px',
      borderRadius: '12px',
      boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
    },
    summaryValue: {
      fontSize: '24px',
      fontWeight: '700',
      marginBottom: '8px'
    },
    summaryLabel: {
      fontSize: '14px',
      color: '#666'
    }
  };

  if (loading) {
    return (
      <div style={styles.container}>
        <div style={styles.header}>
          <Receipt sx={{ fontSize: '32px', color: '#e67e22' }} />
          <h1 style={styles.title}>Expenses</h1>
        </div>
        <LinearProgress />
      </div>
    );
  }

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs}>
      <div style={styles.container}>
        {/* Header */}
        <div style={styles.header}>
          <Receipt sx={{ fontSize: '32px', color: brandColors.primary }} />
          <h1 style={{ ...styles.title, color: brandColors.textPrimary }}>Expenses</h1>
          <Button
            variant="contained"
            startIcon={<Add />}
            onClick={() => handleOpenDialog()}
            sx={{ 
              marginLeft: 'auto',
              backgroundColor: brandColors.primary,
              '&:hover': { backgroundColor: brandColors.accent }
            }}
          >
            Add Expense
          </Button>
        </div>

        <p style={styles.subtitle}>
          Track and manage your business expenses. Upload receipts and categorize expenses for easy reporting.
        </p>

        {error && (
          <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        {/* Summary Cards */}
        <div style={styles.summaryGrid}>
          <Card sx={styles.summaryCard}>
            <CardContent>
              <AttachMoney sx={{ fontSize: 32, color: brandColors.primary, mb: 1 }} />
              <Typography sx={{ ...styles.summaryValue, color: brandColors.primary }}>
                {formatCurrency(totalExpenses, 'USD')}
              </Typography>
              <Typography sx={styles.summaryLabel}>Total Expenses</Typography>
            </CardContent>
          </Card>

          <Card sx={styles.summaryCard}>
            <CardContent>
              <Receipt sx={{ fontSize: 32, color: '#3498db', mb: 1 }} />
              <Typography sx={{ ...styles.summaryValue, color: '#3498db' }}>
                {expenses.length}
              </Typography>
              <Typography sx={styles.summaryLabel}>Total Records</Typography>
            </CardContent>
          </Card>

          <Card sx={styles.summaryCard}>
            <CardContent>
              <Category sx={{ fontSize: 32, color: '#27ae60', mb: 1 }} />
              <Typography sx={{ ...styles.summaryValue, color: '#27ae60' }}>
                {new Set(expenses.map(e => e.category)).size}
              </Typography>
              <Typography sx={styles.summaryLabel}>Categories</Typography>
            </CardContent>
          </Card>

          <Card sx={styles.summaryCard}>
            <CardContent>
              <Business sx={{ fontSize: 32, color: '#9b59b6', mb: 1 }} />
              <Typography sx={{ ...styles.summaryValue, color: '#9b59b6' }}>
                {expenses.filter(e => e.status === 'pending').length}
              </Typography>
              <Typography sx={styles.summaryLabel}>Pending Approval</Typography>
            </CardContent>
          </Card>
        </div>

        {/* Expenses Table */}
        <Paper sx={{ p: 3 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
            <Receipt />
            <Typography variant="h6" sx={{ fontWeight: 600 }}>
              Expense Records
            </Typography>
            <Chip
              label={`${expenses.length} expenses`}
              size="small"
              color="primary"
              variant="outlined"
            />
          </Box>

          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell sx={{ fontWeight: 600 }}>Date</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Description</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Category</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Amount</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Project</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Status</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {expenses.map((expense) => (
                  <TableRow key={expense.id}>
                    <TableCell>
                      {dayjs(expense.date).format('MMM DD, YYYY')}
                    </TableCell>
                    <TableCell>
                      <Typography variant="subtitle2" sx={{ fontWeight: 500 }}>
                        {expense.description}
                      </Typography>
                      {expense.notes && (
                        <Typography variant="caption" color="text.secondary">
                          {expense.notes}
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={expense.category}
                        size="small"
                        variant="outlined"
                        color="primary"
                      />
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" sx={{ fontWeight: 500 }}>
                        {formatCurrency(expense.amount, expense.currency)}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      {expense.projectName ? (
                        <Typography variant="body2">
                          {expense.projectName}
                        </Typography>
                      ) : (
                        <Typography variant="body2" color="text.secondary">
                          No project
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={expense.status.charAt(0).toUpperCase() + expense.status.slice(1)}
                        size="small"
                        color={getStatusColor(expense.status) as any}
                        variant="filled"
                      />
                    </TableCell>
                    <TableCell>
                      <Box sx={{ display: 'flex', gap: 1 }}>
                        {expense.receiptUrl && (
                          <Tooltip title="View Receipt">
                            <IconButton
                              size="small"
                              onClick={() => window.open(expense.receiptUrl, '_blank')}
                            >
                              <Visibility />
                            </IconButton>
                          </Tooltip>
                        )}
                        <Tooltip title="Edit">
                          <IconButton
                            size="small"
                            onClick={() => handleOpenDialog(expense)}
                          >
                            <Edit />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Delete">
                          <IconButton
                            size="small"
                            onClick={() => handleDelete(expense.id)}
                            color="error"
                          >
                            <Delete />
                          </IconButton>
                        </Tooltip>
                      </Box>
                    </TableCell>
                  </TableRow>
                ))}
                {expenses.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={7} align="center" sx={{ py: 4 }}>
                      <Typography variant="body1" color="text.secondary">
                        No expenses recorded yet. Click "Add Expense" to get started.
                      </Typography>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>

        {/* Add/Edit Expense Dialog */}
        <Dialog open={dialogOpen} onClose={handleCloseDialog} maxWidth="md" fullWidth>
          <DialogTitle>
            {editingExpense ? 'Edit Expense' : 'Add New Expense'}
          </DialogTitle>
          <DialogContent>
            <Box sx={{ display: 'grid', gap: 3, mt: 2 }}>
              <Box sx={{ display: 'grid', gridTemplateColumns: '2fr 1fr', gap: 2 }}>
                <TextField
                  label="Description"
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  required
                  fullWidth
                />
                <TextField
                  label="Amount"
                  type="number"
                  value={formData.amount}
                  onChange={(e) => setFormData({ ...formData, amount: e.target.value })}
                  required
                  InputProps={{
                    startAdornment: <InputAdornment position="start">$</InputAdornment>,
                  }}
                />
              </Box>

              <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 2 }}>
                <FormControl required>
                  <InputLabel>Currency</InputLabel>
                  <Select
                    value={formData.currency}
                    onChange={(e) => setFormData({ ...formData, currency: e.target.value })}
                    label="Currency"
                  >
                    {currencies.map((currency) => (
                      <MenuItem key={currency} value={currency}>
                        {currency}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>

                <FormControl required>
                  <InputLabel>Category</InputLabel>
                  <Select
                    value={formData.category}
                    onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                    label="Category"
                  >
                    {expenseCategories.map((category) => (
                      <MenuItem key={category} value={category}>
                        {category}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>

                <DatePicker
                  label="Date"
                  value={formData.date}
                  onChange={(date) => setFormData({ ...formData, date })}
                  slotProps={{ textField: { required: true } }}
                />
              </Box>

              <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2 }}>
                <FormControl>
                  <InputLabel>Project (Optional)</InputLabel>
                  <Select
                    value={formData.projectId}
                    onChange={(e) => setFormData({ ...formData, projectId: e.target.value })}
                    label="Project (Optional)"
                  >
                    <MenuItem value="">No project</MenuItem>
                    {projects.map((project) => (
                      <MenuItem key={project.id} value={project.id}>
                        {project.name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>

                <FormControl>
                  <InputLabel>Client (Optional)</InputLabel>
                  <Select
                    value={formData.clientId}
                    onChange={(e) => setFormData({ ...formData, clientId: e.target.value })}
                    label="Client (Optional)"
                  >
                    <MenuItem value="">No client</MenuItem>
                    {clients.map((client) => (
                      <MenuItem key={client.id} value={client.id}>
                        {client.name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Box>

              <TextField
                label="Notes (Optional)"
                value={formData.notes}
                onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                multiline
                rows={3}
                fullWidth
              />

              <Box sx={{ border: '2px dashed #e0e0e0', borderRadius: 2, p: 3, textAlign: 'center' }}>
                <CloudUpload sx={{ fontSize: 48, color: '#ccc', mb: 2 }} />
                <Typography variant="body1" color="text.secondary" gutterBottom>
                  Receipt Upload (Coming Soon)
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Drag and drop receipt images or click to browse
                </Typography>
              </Box>
            </Box>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCloseDialog}>Cancel</Button>
            <Button
              onClick={handleSubmit}
              variant="contained"
              disabled={!formData.description || !formData.amount || !formData.category}
              sx={{ backgroundColor: brandColors.primary, '&:hover': { backgroundColor: brandColors.accent } }}
            >
              {editingExpense ? 'Update' : 'Add'} Expense
            </Button>
          </DialogActions>
        </Dialog>
      </div>
    </LocalizationProvider>
  );
};

export default Expenses;
